import React from "react";
import { RouteObject } from "react-router-dom";

import { PageError } from "../../components/Error";

import AdminEmployees from "../../pages/AdminEmployees";
import AIAuthoringAssistant, {
	aIAuthoringAssistantSettingsLoader,
} from "../../pages/Administration/AIInsights/AIAuthoringAssistant";
import AIEngagementAnalysisSettings, {
	aIEngagementAnalysisSettingsLoader,
} from "../../pages/Administration/AIInsights/AIEngagementAnalysis";
import AIMeetingWorkspaceInsights, {
	aIMeetingWorkspaceInsightsSettingsLoader,
} from "../../pages/Administration/AIInsights/AIMeetingWorkspaceInsights";
import AIReviewCycleInsightsSettings, {
	aIReviewCycleInsightsSettingsLoader,
} from "../../pages/Administration/AIInsights/AIReviewCycleInsights";
import AIReviewSummarization, {
	aIReviewSummarizationSettingsLoader,
} from "../../pages/Administration/AIInsights/AIReviewSummarization";
import AISurveyInsights, {
	aISurveyInsightsSettingsLoader,
} from "../../pages/Administration/AIInsights/AISurveyInsights";
import AuthenticationPage from "../../pages/Administration/Authentication";
import ViewApiKey from "../../pages/Administration/Authentication/pages/APIKey/ViewApiKey";
import { companySettingsLoader } from "../../pages/Administration/Authentication/pages/CompanySettings";
import CalendarIntegration, { calendarIntegrationSettingsLoader } from "../../pages/Administration/CalendarIntegration";
import ConversationalAISettings, { conversationalAISettingsLoader } from "../../pages/Administration/ConversationalAI";
import AdminEditReviewTemplate from "../../pages/Administration/EditReviewTemplate";
import EngagementAdminSettings, { engagementSettingsLoader } from "../../pages/Administration/Engagement";
import Log from "../../pages/Administration/Log";
import LogDetails from "../../pages/Administration/LogDetails";
import TeamsIntegrationSetup from "../../pages/Administration/MicrosoftTeamsIntegration";
import NotificationsTemplate from "../../pages/Administration/NotificationsTemplate";
import EditForm, { adminReviewFormLoader } from "../../pages/Administration/Reviews/EditForm";
import AdminReviewsForms, { adminReviewsFormsLoader } from "../../pages/Administration/Reviews/Forms";
import ViewSyncLog, { scimLogLoader } from "../../pages/Administration/SCIMSettings/ScimLogs";
import ViewSCIMToken from "../../pages/Administration/SCIMSettings/ViewSCIMToken";
import SCIMSettings from "../../pages/Administration/SCIMSettings/index";
import SftpSettings from "../../pages/Administration/Sftp";
import SetUpSFTP from "../../pages/Administration/Sftp/SftpSettings";
import Billing, { billingLoader } from "../../pages/Billing";
import BillingErrorPage from "../../pages/Billing/BillingErrorPage";
import CompanyDirectory from "../../pages/CompanyDirectory";
import CompanyFeedback from "../../pages/CompanyFeedback";
import CompanyMeetingsPage, { companyMeetingsLoader } from "../../pages/CompanyMeetings";
import CompanyResources from "../../pages/CompanyResources";
import Dashboard from "../../pages/Dashboard";
import DeactivateEmployees from "../../pages/DeactivateEmployees";
import Documents from "../../pages/Documents";
import EditEmployee from "../../pages/EditEmployee/EditEmployee";
import EmployeeDetails, { viewEmployeeDetailsLoader } from "../../pages/EmployeeDetails/EmployeeDetails";
import ViewFieldHistory from "../../pages/EmployeeDetails/ViewFieldHistory";
import EngagementCohorts from "../../pages/Engagement/pages/Cohorts";
import EngagementFactors from "../../pages/Engagement/pages/Factors";
import EngagementOverview from "../../pages/Engagement/pages/Overview";
import EngagementQuestions from "../../pages/Engagement/pages/Questions";
import EngagementEnps from "../../pages/Engagement/pages/eNPS";
import ExtendSessionPage from "../../pages/ExtendSession";
import Feedback from "../../pages/Feedback";
import CompanyGoalsPage from "../../pages/Goals/Company";
import GoalsCreate from "../../pages/Goals/Create";
import EmployeeGoals from "../../pages/Goals/Employee";
import SingleGoal from "../../pages/Goals/Single";
import ViewCreateGoalsJob from "../../pages/Goals/ViewJob";
import ViewGroup from "../../pages/Group";
import IntegrationsMain from "../../pages/Integrations/Main";
import ViewAllJobs from "../../pages/Jobs/All";
import ViewJob from "../../pages/Jobs/Single";
import WorkspaceDetailsRouter, {
	meetingWorkspaceDetailsLoader,
} from "../../pages/Meetings/WorkspaceDetails/WorkspaceDetailsRouter";
import MeetingWorkspaces, { meetingWorkspacesLoader } from "../../pages/Meetings/Workspaces";
import ReportAnalytics from "../../pages/ReportAnalytics/pages/Home";
import AnalyticsReport from "../../pages/ReportAnalytics/pages/Report";
import ReviewCycleAddSubjects from "../../pages/ReviewCycle/AddSubjects";
import ReviewCycleAssignmentLog, { assignmentLogLoader } from "../../pages/ReviewCycle/AssignmentLog";
import CreateReviewCycle from "../../pages/ReviewCycle/Create";
import ReviewCycleDetails, { reviewCycleDetailsLoader } from "../../pages/ReviewCycle/Details";
import EditReviewCycle from "../../pages/ReviewCycle/Edit";
import EditAutoflow from "../../pages/ReviewCycle/EditAutoflow";
import CompanyReviews from "../../pages/Reviews/Company";
import PerformanceGraphsWrapper from "../../pages/Reviews/Company/SubPages/PerformanceGraphs";
import Review from "../../pages/Reviews/Review";
import UserReviews from "../../pages/Reviews/UserReviews";
import SurveyPage, { surveyLoader } from "../../pages/Surveys/pages/SurveyAuthoring";
import SurveyDetailsPage, { surveyDetailsLoader } from "../../pages/Surveys/pages/SurveyDetails";
import SurveysPage, { surveysLoader } from "../../pages/Surveys/pages/Surveys";
import UserFeedback from "../../pages/UserFeedback";
import UserSettings from "../../pages/UserSettings";
import { calendarViewLoader } from "../../pages/UserSettings/Views/Calendar";
import ViewReview from "../../pages/ViewReview";
import ViewReviewForm from "../../pages/ViewReviewForm";
import LaunchWorkflow from "../../pages/Workflows/Launch";
import CreateWorkflowsTemplate from "../../pages/Workflows/TemplateCreate";
import ViewWorkflows from "../../pages/Workflows/ViewWorkflows";

import RouterTags from "../../constants/RouterTags";
import App from "../app";
import { ReactRouterCompatibilityLoader } from "./compatibility";

const routes = [
	{ path: "*", element: <App />, errorElement: <PageError /> },
	{
		path: "/",
		element: <App />,
		errorElement: <PageError />,
		loadingElement: <ReactRouterCompatibilityLoader />,
		children: [
			{ index: true, element: <Dashboard />, handle: { activeNavigation: "user-dashboard" } },
			{
				path: "user/:userID",
				element: <Dashboard />,
				handle: { tags: [RouterTags.USER_PAGE], activeNavigation: "user-dashboard" },
			},
			{
				path: "view-feedback/:feedbackId",
				element: <Feedback />,
				handle: {
					tags: [],
					activeNavigation: "user-feedback",
					legacyPageName: "viewFeedback",
				},
			},
			{
				path: "feedback/:userId/:view?",
				element: <UserFeedback />,
				handle: {
					tags: [RouterTags.USER_PAGE],
					activeNavigation: "user-feedback",
					legacyPageName: "userFeedback",
				},
			},
			{
				path: "directory",
				element: <CompanyDirectory />,
				handle: { activeNavigation: "company-directory" },
			},
			{
				path: "review-cycle/create",
				element: <CreateReviewCycle />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "review-cycle/:cycleId",
				element: <ReviewCycleDetails />,
				handle: { activeNavigation: "company-reviews" },
				loader: reviewCycleDetailsLoader,
			},
			{
				path: "review-cycle/:cycleId/edit",
				element: <EditReviewCycle />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ReviewCycleEdit" },
			},
			{
				path: "review-cycle/:cycleId/add",
				element: <ReviewCycleAddSubjects />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ReviewCycleAddSubjects" },
			},
			{
				path: "review-cycle/:cycleId/edit-automation",
				element: <EditAutoflow />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "review-cycle/:cycleId/assignment-log",
				element: <ReviewCycleAssignmentLog />,
				handle: { activeNavigation: "company-reviews" },
				loader: assignmentLogLoader,
			},
			{
				path: "super-user-admin/employees/:companyId/",
				element: <AdminEmployees />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/employees",
				element: <AdminEmployees />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "super-user-admin/sftp/:companyId",
				element: <SftpSettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/sftp",
				element: <SftpSettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "administration/sftp/create", element: <SetUpSFTP />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "administration/sftp/edit/:integrationID",
				element: <SetUpSFTP />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "super-user-admin/microsoft-teams-setup/:companyId",
				element: <TeamsIntegrationSetup />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/microsoft-teams-setup",
				element: <TeamsIntegrationSetup />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "super-user-admin/sftp/:companyId", element: <Log />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{ path: "administration/sync-log", element: <Log />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "administration/sync-log/:integrationId",
				element: <LogDetails />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/bulk-deactivate",
				element: <DeactivateEmployees />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/authentication-settings/:settingsPage?",
				element: <AuthenticationPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: async ({ params }) => {
					if (params.settingsPage === "company") {
						return companySettingsLoader();
					}
					return null;
				},
			},
			{
				path: "administration/authentication-settings/api/:apiId",
				element: <ViewApiKey />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "administration/scim/", element: <SCIMSettings />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "administration/scim/:scimId",
				element: <ViewSCIMToken />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/scim/:scimId/sync-log",
				element: <ViewSyncLog />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: scimLogLoader,
			},
			{
				path: "administration/ai-insights/ai-authoring-assistant",
				element: <AIAuthoringAssistant />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aIAuthoringAssistantSettingsLoader,
			},
			{
				path: "administration/ai-insights/ai-review-summarization",
				element: <AIReviewSummarization />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aIReviewSummarizationSettingsLoader,
			},
			{
				path: "administration/ai-insights/ai-review-cycle-insights",
				element: <AIReviewCycleInsightsSettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aIReviewCycleInsightsSettingsLoader,
			},
			{
				path: "administration/ai-insights/ai-engagement-analysis",
				element: <AIEngagementAnalysisSettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aIEngagementAnalysisSettingsLoader,
			},
			{
				path: "administration/ai-insights/ai-meeting-workspace-insights",
				element: <AIMeetingWorkspaceInsights />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aIMeetingWorkspaceInsightsSettingsLoader,
			},
			{
				path: "administration/ai-insights/ai-survey-insights",
				element: <AISurveyInsights />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: aISurveyInsightsSettingsLoader,
			},
			{
				path: "administration/conversational-ai",
				element: <ConversationalAISettings />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: conversationalAISettingsLoader,
			},
			{
				path: "administration/engagement",
				element: <EngagementAdminSettings tab="main" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts",
				element: <EngagementAdminSettings tab="cohorts" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/create-cohort",
				element: <EngagementAdminSettings tab="create-cohort" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/duplicate-cohort/:cohortID",
				element: <EngagementAdminSettings tab="duplicate-cohort" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID/details",
				element: <EngagementAdminSettings tab="edit-cohort-details" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID/add-employees",
				element: <EngagementAdminSettings tab="edit-cohort-employees-add-employees" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/cohorts/:cohortID",
				element: <EngagementAdminSettings tab="edit-cohort-employees" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/questions",
				element: <EngagementAdminSettings tab="questions" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},
			{
				path: "administration/engagement/excluded-employees",
				element: <EngagementAdminSettings tab="excluded-employees" />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: engagementSettingsLoader,
			},

			{
				path: "edit-review-template/:templateID?",
				element: <AdminEditReviewTemplate />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "super-user-admin/billing/:companyID",
				element: <Billing />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "administration/billing",
				element: <Billing />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: billingLoader,
				errorElement: <BillingErrorPage />,
			},
			{
				path: "administration/calendar-integration",
				element: <CalendarIntegration />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: calendarIntegrationSettingsLoader,
			},
			{
				path: "super-user-admin/:path/*",
				element: null,
				handle: { tags: [RouterTags.SETTINGS, RouterTags.NO_FEEDBACK, RouterTags.USES_TEMPLATE] },
			},
			{ path: "group/employees/:groupId", element: <ViewGroup />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "/doc-signing/*",
				element: null,
				handle: { tags: [RouterTags.NO_FEEDBACK, RouterTags.USES_TEMPLATE] },
			},
			{ path: "edit-workflow", element: <CreateWorkflowsTemplate />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "edit-workflow/:workflowTemplateID",
				element: <CreateWorkflowsTemplate />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "launch-workflow", element: <LaunchWorkflow />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "company/workflows",
				element: <ViewWorkflows />,
				handle: { tags: [RouterTags.NO_FEEDBACK], activeNavigation: "company-workflows" },
			},
			{ path: "jobs", element: <ViewAllJobs />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "jobs/:jobID",
				element: <ViewJob />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "ViewJob" },
			},
			{
				path: "goal/wizard",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "create", legacyPageName: "CreateGoal" },
			},
			{
				path: "goal/:goalID/copy",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "copy", legacyPageName: "CopyGoal" },
			},
			{
				path: "goal/:goalID/edit",
				element: <GoalsCreate />,
				handle: { tags: [RouterTags.NO_FEEDBACK], action: "edit", legacyPageName: "EditGoal" },
			},
			{ path: "goal/build/:jobID", element: <ViewCreateGoalsJob /> },
			{
				path: "integrations/:page?",
				element: <IntegrationsMain />,
				handle: { tags: [RouterTags.NO_FEEDBACK], legacyPageName: "IntegrationsSetup" },
			},
			{
				path: "company/goals/:page?/:goal_id?",
				element: <CompanyGoalsPage />,
				handle: { activeNavigation: "company-goals" },
			},
			{
				path: "goal/:goal_id?",
				element: <SingleGoal />,
				handle: { activeNavigation: "user-goals" },
			},
			{ path: "extend-session", element: <ExtendSessionPage /> },
			{
				path: "settings/:page?",
				element: <UserSettings />,
				loader: async ({ params }) => {
					// Only run calendar data loader for calendar page
					if (params.page === "calendar") {
						return calendarViewLoader();
					}
					return null;
				},
			},
			{ path: "administration/notifications", element: <NotificationsTemplate /> },
			{
				path: "employees/:employeeID/goals/:page?/",
				element: <EmployeeGoals />,
				handle: { activeNavigation: "user-goals" },
			},
			{ path: "employees/goals", element: <EmployeeGoals />, handle: { activeNavigation: "user-goals" } },
			{
				path: "company/reviews/graphs/",
				element: <PerformanceGraphsWrapper />,
			},
			{
				path: "company/reviews/:page?/",
				element: <CompanyReviews />,
				handle: { activeNavigation: "company-reviews" },
			},

			{ path: "start-review/:paramA?/", element: null, handle: { tags: [RouterTags.USES_TEMPLATE] } },

			{
				path: "org-chart",
				element: null,
				handle: { tags: [RouterTags.USES_TEMPLATE], activeNavigation: "company-org-chart" },
			},
			{
				path: "company/feedback",
				element: <CompanyFeedback />,
				handle: { activeNavigation: "company-feedback" },
			},
			{ path: "company/reports/all", element: null, handle: { activeNavigation: "company-reports" } },
			{ path: "company/reports/collected", element: null, handle: { activeNavigation: "company-reports" } },
			{ path: "reviews", element: <UserReviews /> },
			{
				path: "reviews/:paramA?/:paramB?",
				element: <UserReviews />,
				handle: { tags: [RouterTags.USER_PAGE], activeNavigation: "user-reviews" },
			},
			{
				path: "view-review/:reviewID",
				element: <ViewReview />,
				handle: { legacyPageName: "ViewReview", tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "view-review/:reviewID/awaiting-approval",
				element: <ViewReview />,
				handle: {
					legacyPageName: "ViewReviewAwaitingApproval",
					tags: [RouterTags.USES_TEMPLATE_AND_REACT],
				},
			},
			{ path: "view-field-history/:fieldID/:userID", element: <ViewFieldHistory /> },
			{
				path: "details/:userID",
				element: <EmployeeDetails />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: viewEmployeeDetailsLoader,
			},
			{
				path: "administration/reviews/forms/",
				element: <AdminReviewsForms />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
				},
				loader: adminReviewsFormsLoader,
			},
			{
				path: "edit-review-form/:baseFormId",
				element: <EditForm />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK, RouterTags.HIDE_ADMIN_NAV],
				},
				loader: adminReviewFormLoader,
			},
			{
				path: "super-user-admin/reviews/forms/:companyId",
				element: <AdminReviewsForms />,
				handle: {
					tags: [RouterTags.NO_FEEDBACK],
				},
				loader: adminReviewsFormsLoader,
			},
			{ path: "/edit-employee/:userID", element: <EmployeeDetails />, loader: viewEmployeeDetailsLoader },
			{ path: "view-field-history/:fieldID/:userID", element: <EditEmployee /> },
			{
				path: "add-employee",
				element: <EditEmployee />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "add-employee/:companyID",
				element: <EditEmployee />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{
				path: "analytics/report/:reportID?",
				element: <AnalyticsReport />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "analytics", element: <ReportAnalytics /> },
			{
				path: "engagement/overview",
				element: <EngagementOverview />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "engagement/cohorts", element: <EngagementCohorts />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{ path: "engagement/factors", element: <EngagementFactors />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{ path: "engagement/enps", element: <EngagementEnps />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "engagement/questions",
				element: <EngagementQuestions />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
			},
			{
				path: "review/:reviewFormID",
				element: <ViewReviewForm />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{ path: "documents", element: <Documents />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "documents/:userID",
				element: <Documents />,
				handle: { activeNavigation: "user-documents", tags: [RouterTags.NO_FEEDBACK] },
			},
			{ path: "company/documents", element: <Documents />, handle: { tags: [RouterTags.NO_FEEDBACK] } },
			{
				path: "company/resources",
				element: <CompanyResources />,
				handle: { tags: [RouterTags.NO_FEEDBACK], activeNavigation: "company-resources" },
			},
			{
				path: "company/links",
				element: <CompanyResources />,
				handle: { tags: [RouterTags.NO_FEEDBACK], activeNavigation: "company-resources" },
			},
			{
				path: "review/:reviewID/",
				element: <Review />,
				handle: { tags: [RouterTags.USES_TEMPLATE_AND_REACT] },
			},
			{ path: "iframe/review/:reviewID/", element: <Review /> },
			{
				path: "/meeting-workspaces",
				element: <MeetingWorkspaces />,
				handle: { tags: [RouterTags.NO_FEEDBACK], activeNavigation: "meeting-workspaces" },
				loader: meetingWorkspacesLoader,
			},
			{
				path: "/meeting-workspaces/:workspaceId",
				element: <WorkspaceDetailsRouter />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: meetingWorkspaceDetailsLoader,
			},
			{
				path: "company/meetings",
				element: <CompanyMeetingsPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK], activeNavigation: "company-meetings" },
				loader: companyMeetingsLoader,
			},
			{
				path: "surveys",
				element: <SurveysPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: surveysLoader,
			},
			{
				// allow query params to be passed to the survey details page
				path: "surveys/:surveyId",
				element: <SurveyDetailsPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: surveyDetailsLoader,
			},
			{
				path: "survey/:surveyLinkId",
				element: <SurveyPage />,
				handle: { tags: [RouterTags.NO_FEEDBACK] },
				loader: surveyLoader,
			},
		],
	},
] as RouteObject[];

export default routes;
